import { IconName } from "../style";

export type PropOption = {
  /**
   * The value of the option. This is the value that will be returned when the option is selected.
   */
  value: string;

  /**
   * The search string for the option. This is used to filter the options. If not provided, the value will be used.
   */
  search?: string[];

  /**
   * The title of the option. This is used to display the option. If not provided, the value will be used.
   */
  title?: React.ReactNode;

  /**
   * The subtitle of the option. Used a subtext when rendering the option.
   */
  subtitle?: React.ReactNode;

  /**
   * The icon of the option. Used to display an icon next to the title.
   */
  icon?: IconName;

  /**
   * The group of the option. Used to group options with the same value together in the dropdown.
   */
  group?: string;

  /**
   * Whether the option is disabled. If true, the option will not be selectable.
   */
  disabled?: boolean;

  /**
   * The right side of the option. Anything can be used here.
   */
  rightSide?: React.ReactNode;

  /**
   * Any additional metadata that can be used to identify the option.
   */
  meta?: Record<string, unknown>;
};

export type PropSelected = PropOption & {
  state?: "loading" | "error" | "valid";
};

type Selected<T extends PropSelected = PropSelected> = {
  title: React.ReactNode;
  state: "loading" | "error" | "valid";
} & T;

type Option<T extends PropOption = PropOption> = Omit<T, "group"> & {
  title: React.ReactNode;
  search: string[];
  selected: boolean;
  disabled: boolean;
};

type Group<T extends Option> = {
  title?: string;
  options: Option<T>[];
};

type PropOptionsBySearch = { [key: string]: PropOption };

const findBySearchLookupCache = new WeakMap<PropOption[], PropOptionsBySearch>();

function getSearch(option: PropOption): string[] {
  return (option.search ?? [option.value]).map((s) => s.toLowerCase());
}

const transformPropSelected = <T extends PropSelected>(selected: T): Selected<T> => {
  return {
    ...selected,
    title: selected.title ?? selected.value,
    state: selected.state ?? "valid",
  };
};

const transformPropOption = <T extends PropOption>(option: T): Option<T> => {
  // Intentionally drop the group from the option.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { group, ...rest } = option;

  return {
    ...rest,
    title: rest.title ?? rest.value,
    disabled: rest.disabled ?? false,
    selected: false,
    search: getSearch(rest),
  };
};

type BinParams = {
  filterSearch: string;
  filterSelectedFromItems: boolean;
};

type GroupStartRenderItem = {
  type: "group-start";
  title: string;
};

type OptionRenderItem<T extends PropOption> = {
  type: "option";
  option: Option<T>;
};

type GroupEndRenderItem = {
  type: "group-end";
};

export type PropOptionRenderItem<T extends PropOption = PropOption> = GroupStartRenderItem | OptionRenderItem<T> | GroupEndRenderItem;

type BinResult<T extends PropOption, U extends PropSelected> = {
  items: PropOptionRenderItem<Option<T>>[];
  selected: Selected<U>[];
};

export const PropOption = {
  /**
   * Transforms a list of prop options into a list of renderable items, ordered by their group id.
   */
  bin<T extends PropOption, U extends PropSelected>(options: T[], selected0: U[], params: BinParams): BinResult<T, U> {
    type TOption = Option<T>;
    type TGroup = Group<TOption>;
    type GroupsMap = { [groupName: string]: TGroup };

    const { filterSearch, filterSelectedFromItems } = params;
    const filterSearchLowerCase = filterSearch.toLowerCase();
    const selectedMap: { [value: string]: true } = {};

    /**
     * Split this into two lists, because we always want items that are "loading" or
     * "error" to appear at the end of the list.
     */
    const validSelected: Selected<U>[] = [];
    const loadingOrErrorSelected: Selected<U>[] = [];

    for (const option of selected0) {
      const next = transformPropSelected(option);

      selectedMap[next.value] = true;

      if (next.state === "valid") {
        validSelected.push(next);
      } else {
        loadingOrErrorSelected.push(next);
      }
    }

    /**
     * Join these two lists together with the validated items appearing first.
     */
    const selected = [...validSelected, ...loadingOrErrorSelected];

    const groupedMap: GroupsMap = {};
    const ungrouped: TGroup = { options: [] };
    const optionsBySearchMap: PropOptionsBySearch = {};

    for (const option of options) {
      const { group } = option;

      const next = transformPropOption(option);
      next.selected = selectedMap[next.value] !== undefined;

      /**
       * Keep track of the options by their search string so that we can quickly find them by key
       * when we need to make an exact match.
       */
      for (const search of next.search) {
        if (!option.disabled) {
          optionsBySearchMap[search] = option;
        }
      }

      /**
       * We indiscriminately add a grouping _just in case_ all of the options in this group are selected.
       * We then have to filter out empty groups later, but we don't want the order of the groups to change
       * between renders. The order that they are displayed is based on the order that they are added to the map.
       */
      if (group !== undefined) {
        groupedMap[group] ??= { title: group, options: [] };
      }

      if (filterSelectedFromItems && next.selected) {
        continue;
      }

      if (!next.search.find((s) => s.includes(filterSearchLowerCase))) {
        continue;
      }

      if (group !== undefined) {
        groupedMap[group].options.push(next);
      } else {
        ungrouped.options.push(next);
      }
    }

    const items: PropOptionRenderItem<Option<T>>[] = [];

    for (const group of Object.values(groupedMap)) {
      if (group.options.length === 0) {
        continue;
      }

      items.push({
        type: "group-start",
        title: group.title ?? "",
      });

      for (const option of group.options) {
        items.push({
          type: "option",
          option,
        });
      }

      items.push({
        type: "group-end",
      });
    }

    for (const option of ungrouped.options) {
      items.push({
        type: "option",
        option,
      });
    }

    findBySearchLookupCache.set(options, optionsBySearchMap);

    return {
      items,
      selected,
    };
  },

  fullMatchBySearch<T extends PropOption>(options: T[], search: string): T | null {
    const searchLowerCase = search.toLowerCase();

    const map = findBySearchLookupCache.get(options);

    if (map) {
      return (map[searchLowerCase] as T) ?? null;
    }

    return options.filter((o) => !o.disabled).find((o) => getSearch(o).includes(searchLowerCase)) ?? null;
  },
};

export type PublicPropOption = PropOption;
export type PublicPropSelected = PropSelected;

export const PublicPropOption = {};
